import React, { useEffect, useMemo, useRef, useState } from "react";

import Hls from "hls.js";
import mpegts from "mpegts.js";

import { CHANNEL_REGISTRY } from "./tv/channelRegistry";

import pjLogo from "./assets/posterjunkielogin.png";

import {
  CHANNELS,
  TIME_SLOTS,
  CHANNEL_COL_WIDTH,
  SLOT_WIDTH,
  getInitialSelection,
  CONFIG_BY_NUMBER,
} from "./tv/channels";

import { searchTMDBTV, tmdbImage } from "./api/tmdb";
import {
  setMediaPlaying,
  setMediaPaused,
  setMediaBuffering,
  updateMediaProgress,
  setMediaMuted,
} from "./os/MediaSessionManager";

export default function TVGuide() {
  const [selected, setSelected] = useState({ ...getInitialSelection(), locked: false });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [time, setTime] = useState("");
  const [tmdbData, setTmdbData] = useState(null);

  const guideRef = useRef(null);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const lastGuideFocusRef = useRef(null);

  const [guidePlayerMode, setGuidePlayerMode] = useState(false);
  const [previewMuted, setPreviewMuted] = useState(true);
  const [previewFull, setPreviewFull] = useState(false);

  const lastLoadedStreamRef = useRef(null);
  const [plutoStreams, setPlutoStreams] = useState([]);
  const [dynamicTubiChannels, setDynamicTubiChannels] = useState([]);
  const [dynamicPjtvChannels, setDynamicPjtvChannels] = useState([]);
  const [pjtvSchedule, setPjtvSchedule] = useState({});
  const [pjtvGuideAnchor] = useState(() => Date.now());

  // ── CLOCK (drives re-render every second) ──
useEffect(() => {
  const tick = () => setTime(Date.now()); // store timestamp, not formatted string

  tick();
  const interval = setInterval(tick, 30000);

  return () => clearInterval(interval);
}, []);


// ── LIVE TIME SLOTS ──
const liveTimeSlots = useMemo(() => {
  const slots = [];

  const base = new Date();

  base.setMinutes(base.getMinutes() - (base.getMinutes() % 30), 0, 0);

  for (let i = 0; i < TIME_SLOTS.length; i++) {
    const slotTime = new Date(base.getTime() + i * 30 * 60 * 1000);

    slots.push(
      slotTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    );
  }

  return slots;
}, [time]);

const displayTime = new Date().toLocaleTimeString("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

const guideChannels = useMemo(() => {
  const map = new Map();

  [...CHANNELS, ...dynamicTubiChannels, ...dynamicPjtvChannels].forEach((channel) => {
    if (channel.hidden) return;

    const key = String(channel.number);
    const existing = map.get(key);

    // PJTV always wins
    if (channel.type === "PJTV") {
      map.set(key, channel);
      return;
    }

    // Never overwrite PJTV with old analog/Tunarr channel
    if (existing?.type === "PJTV") {
      return;
    }

    if (
      !existing ||
      channel.type === "PLUTO" ||
      channel.type === "TUBI" ||
      channel.type === "DIZQUETV" ||
      channel.streamUrl
    ) {
      map.set(key, channel);
    }
  });

  return [...map.values()]
  .map((channel) => {
    const number = Number(channel.number);

    let fallbackGroup = "Other";

    if (number >= 2 && number <= 13) fallbackGroup = "Broadcast";
    else if (number >= 14 && number <= 39) fallbackGroup = "Cable";
    else if (number >= 40 && number <= 99) fallbackGroup = "Classic Cable";
    else if (number >= 100 && number <= 199) fallbackGroup = "Digital / Premium";
    else if (number >= 200 && number <= 399) fallbackGroup = "Custom Plex";
    else if (number >= 700 && number <= 999) fallbackGroup = "Pluto";
    else if (number >= 1000) fallbackGroup = "Tubi";

    return {
      ...channel,
      group: channel.group || fallbackGroup,
    };
  })
  .sort((a, b) => Number(a.number) - Number(b.number));
}, [dynamicTubiChannels, dynamicPjtvChannels]);

  useEffect(() => {
    const fetchPlutoM3U = async () => {
      try {
        const res = await fetch("/tuner-1-playlist.m3u");
        const text = await res.text();
        const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
        const parsed = [];
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].startsWith("#EXTINF")) {
            const idMatch = lines[i].match(/channel-id="([^"]+)"/);
            const logoMatch = lines[i].match(/tvg-logo="([^"]+)"/);
            const name = lines[i].split(",").pop()?.trim();
            const url = lines[i + 1];
            if (url && url.startsWith("http")) {
              parsed.push({
                id: idMatch ? idMatch[1] : null,
                logo: logoMatch ? logoMatch[1] : null,
                name: name || "",
                url,
              });
            }
          }
        }
        setPlutoStreams(parsed);
      } catch (err) {
        console.error("Failed to load Pluto M3U:", err);
      }
    };
    fetchPlutoM3U();
  }, []);

useEffect(() => {
  const fetchTubiChannels = async () => {
    try {
      const res = await fetch("/tubi-channels.json");
      const data = await res.json();

      setDynamicTubiChannels(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load Tubi channels:", err);
    }
  };

  fetchTubiChannels();
}, []);

useEffect(() => {
  let isMounted = true;

  const buildFallbackPrograms = (channel) => {
    if (channel.type === "PLUTO" || channel.type === "TUBI") {
      return [
        {
          title: channel.displayName || channel.guideLabel || channel.name,
          start: "Live",
          end: "",
          width: TIME_SLOTS.length,
          rating: "TV-G",
          genres: [channel.category || "Live"],
          summary: `${channel.displayName || channel.name} live channel.`,
        },
      ];
    }

    return [
      {
        title: channel.displayName || channel.guideLabel || channel.name,
        start: "",
        end: "",
        width: 4,
        rating: "TV-PG",
        genres: [channel.category || "PJTV"],
        summary: `${channel.displayName || channel.name}`,
      },
    ];
  };

  const fetchPJTVChannels = async () => {
    try {
      let data = [];

      try {
        const pjtvRes = await fetch("/pjtv/channels", { cache: "no-store" });
        const pjtvData = await pjtvRes.json();

        if (pjtvData && Object.keys(pjtvData).length) {
          data = Array.isArray(pjtvData) ? pjtvData : Object.values(pjtvData);
        } else {
          throw new Error("PJTV empty");
        }
      } catch (e) {
        console.warn("Falling back to /guide/channels", e);

        const guideRes = await fetch("/guide/channels", { cache: "no-store" });
        const guideData = await guideRes.json();

        data = Array.isArray(guideData) ? guideData : Object.values(guideData || {});
      }

      const channels = data.map((channel) => {
        const normalized = {
          ...channel,
          type: channel.type || "PJTV",
          category: channel.category || "PJTV",
          displayName: channel.displayName || channel.name,
          guideLabel: channel.guideLabel || channel.name,
          color: channel.color || "#222",
          logo: channel.logo || null,
          logoScale: channel.logoScale || 1,
        };

        return {
          ...normalized,
          programs: channel.programs || buildFallbackPrograms(normalized),
        };
      });

      if (isMounted) {
        setDynamicPjtvChannels(channels);
      }
    } catch (err) {
      console.error("Failed to load PJTV channels:", err);
    }
  };

  fetchPJTVChannels();
  const interval = setInterval(fetchPJTVChannels, 5 * 60 * 1000);

  return () => {
    isMounted = false;
    clearInterval(interval);
  };
}, []);

// 👇 EXISTING
useEffect(() => {
}, [dynamicTubiChannels]);

  useEffect(() => {
  const loadPJTV = async () => {
    const pjtvChannels = guideChannels.filter((ch) => ch.type === "PJTV");

    const schedules = await Promise.all(
      pjtvChannels.map(async (ch) => {
        try {
          const res = await fetch(`/pjtv/channel/${ch.number}/schedule`, { cache: "no-store" });

          if (!res.ok) {
            console.warn(`PJTV channel ${ch.number} failed:`, res.status);
            return [String(ch.number), []];
          }

          const data = await res.json();
          const newSchedule = data.schedule || [];
          
          // Equality guard: compare channel numbers and first few ratingKeys
          const existingSchedule = pjtvSchedule[String(ch.number)] || [];
          const existingKeys = existingSchedule.slice(0, 5).map(s => s.ratingKey).join(",");
          const newKeys = newSchedule.slice(0, 5).map(s => s.ratingKey).join(",");
          
          if (existingKeys === newKeys) {
            return [String(ch.number), existingSchedule];
          }
          
          return [String(ch.number), newSchedule];
        } catch (err) {
          console.warn(`PJTV channel ${ch.number} error:`, err);
          return [String(ch.number), []];
        }
      })
    );

    setPjtvSchedule((prev) => ({
      ...prev,
      ...Object.fromEntries(schedules),
    }));
  };

  loadPJTV();

  // Stable polling: 90 seconds for schedules (not 60s)
  const interval = setInterval(loadPJTV, 30 * 60 * 1000); // every 30 minutes
  return () => clearInterval(interval);
}, [guideChannels]);

  const getCurrentPJTVItem = (channelNumber) => {
  const items = pjtvSchedule[String(channelNumber)] || [];
  const now = Date.now();

  return (
    items.find((item) => {
      const start = new Date(item.start).getTime();
      const end = new Date(item.end).getTime();
      return now >= start && now < end;
    }) || null
  );
};

const getPJTVDisplayPrograms = (channel) => {
  if (channel.type !== "PJTV") return channel.programs || [];

  const items = pjtvSchedule[String(channel.number)] || [];
const now = pjtvGuideAnchor || Date.now();
const GUIDE_SLOT_MINUTES = 5;
const maxSlots = TIME_SLOTS.length * (30 / GUIDE_SLOT_MINUTES);

let usedSlots = 0;
const result = [];

for (const item of items) {
  const endTime = new Date(item.end).getTime();

  if (
    item.kind !== "episode" ||
    !Number.isFinite(endTime) ||
    endTime <= now
  ) {
    continue;
  }

  const start = new Date(item.start);
  const end = new Date(item.end);

  const minutes = Math.max(1, Math.round((end - start) / 60000));
  const width = Math.max(1, Math.ceil(minutes / GUIDE_SLOT_MINUTES));

  if (usedSlots + width > maxSlots) break;

  usedSlots += width;

  result.push({
    title: item.showTitle || "Program",
    subtitle: "",
    start: start.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }),
    end: end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }),
    width,
    rating: item.rating || "TV-PG",
    genres: [item.genres?.[0] || "TV"],
    summary: item.summary || "",
    pjtvItem: item,
  });
}

  return result;
};

  const fallbackChannel = {
  number: "",
  name: "",
  type: "",
  category: "",
  programs: [],
};
const selectedData = useMemo(() => {
  const channel =
    guideChannels.find((c) => c.number === selected.channelNumber) ||
    guideChannels[0] ||
    fallbackChannel;
  const programs =
    channel.type === "PJTV"
      ? getPJTVDisplayPrograms(channel)
      : channel.programs || [];
  
  // For PJTV episodes, use ratingKey for stable programKey matching
  const programKey = selected.programKey;
  
  let program = null;
  
  // FIX: If locked PJTV selection exists, preserve it directly without re-finding from guide
  if (channel.type === "PJTV" && selected.locked && selected.program?.pjtvItem) {
    program = selected.program;
  } else if (programKey) {
    // Try to find by programKey
    program = programs?.find((p) => {
      if (channel.type === "PJTV" && p.pjtvItem?.ratingKey) {
        return `${channel.number}-${p.pjtvItem.ratingKey}` === programKey;
      }
      return `${channel.number}-${p.start}-${p.end}-${p.title}` === programKey;
    });
  }
  
  // Fallback to first program only for unlocked/live selections
  program = program || programs?.[0] || channel.programs?.[0] || null;
  
  return {
    channel: { ...channel, programs },
    program,
  };
}, [selected, guideChannels, pjtvSchedule, pjtvGuideAnchor]);

  const normalizeChannelName = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\bpluto tv\b/g, "")
    .replace(/\bpluto\b/g, "")
    .replace(/\btv\b/g, "")
    .replace(/\bchannel\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const findPlutoStream = (channel) => {
  if (!channel) return null;

  if (channel.plutoId) {
    const byId = plutoStreams.find((s) => s.id === channel.plutoId);
    if (byId) return byId;
  }

  const labels = [
    channel.plutoMatch,
    channel.label,
    channel.name,
    channel.displayName,
  ].filter(Boolean);

  for (const label of labels) {
    const normalized = normalizeChannelName(label);

    const exact = plutoStreams.find((s) => {
      const n = normalizeChannelName(s.name);
      return n === normalized;
    });

    if (exact) return exact;

    const fuzzy = plutoStreams.find((s) => {
      const n = normalizeChannelName(s.name);
      return n.includes(normalized) || normalized.includes(n);
    });

    if (fuzzy) return fuzzy;
  }

  return null;
};

const NETWORK_LOGOS = {
  CBS: "/logos/cbs.png",
  ABC: "/logos/abc.png",
  NBC: "/logos/nbc.png",
  FOX: "/logos/fox.png",
  PBS: "/logos/pbs.png",
};

const normalizeLogoKey = (value) => {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9]/g, "");
};

const getChannelLogo = (channel) => {
  if (!channel) return null;

  // PJTV should use your local custom logos first
  if (channel.type === "PJTV") {
    if (channel.logo) return channel.logo;

    const logoKey =
      channel.logoKey ||
      channel.network ||
      channel.callsign ||
      channel.name ||
      channel.label;

    if (logoKey) {
      return `/logos/${normalizeLogoKey(logoKey)}.png`;
    }
  }

  // Pluto/Tubi should use their native logos first
  if (channel.type === "PLUTO") {
    return channel.tvgLogo || channel.plutoLogo || findPlutoStream(channel)?.logo || channel.logo || null;
  }

  if (channel.type === "TUBI") {
  return (
    channel.tubiLogo ||
    channel.logo ||
    channel.tvgLogo ||
    channel.thumbnail ||
    channel.poster ||
    channel.image ||
    channel.preview ||
    null
  );
}

  // Everything else can use local/direct fallbacks
  if (channel.logo) return channel.logo;
  if (channel.tvgLogo) return channel.tvgLogo;
  if (channel.plutoLogo) return channel.plutoLogo;
  if (channel.tubiLogo) return channel.tubiLogo;
  if (channel.thumbnail) return channel.thumbnail;
  if (channel.poster) return channel.poster;
  if (channel.image) return channel.image;

  if (channel.network && NETWORK_LOGOS[channel.network]) {
    return NETWORK_LOGOS[channel.network];
  }

  return null;
};

  // ── UP NEXT COUNTDOWN STATE ─────────────────────────────────────────────
  const [upNextCountdown, setUpNextCountdown] = useState(0);
  const [upNextProgram, setUpNextProgram] = useState(null);
  const countdownTimerRef = useRef(null);

  // Start countdown when video ends or bumper shows
  useEffect(() => {
    const channel = selectedData.channel;
    const program = selectedData.program;
    
    if (channel.type === "PJTV" && program?.pjtvItem?.kind === "episode") {
      const items = pjtvSchedule[String(channel.number)] || [];
      const now = Date.now();
      
      // Find current episode end time
      const currentEnd = new Date(program.pjtvItem.end).getTime();
      const secondsLeft = Math.max(0, Math.ceil((currentEnd - now) / 1000));
      
      if (secondsLeft > 0 && secondsLeft <= 300) {
        // Find next episode in schedule
        const currentIndex = items.findIndex(item => 
          item.ratingKey === program.pjtvItem.ratingKey
        );
        const nextItem = items[currentIndex + 1];
        
        if (nextItem && nextItem.kind === "episode") {
          setUpNextCountdown(secondsLeft);
          setUpNextProgram({ channel, pjtvItem: nextItem });
        }
      }
    }
    
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [selectedData.channel, selectedData.program, pjtvSchedule]);

  // Countdown tick and autoplay
  useEffect(() => {
    if (upNextCountdown <= 0 || !upNextProgram) return;
    
    countdownTimerRef.current = setInterval(() => {
      setUpNextCountdown(prev => {
        if (prev <= 1) {
          // Countdown complete - trigger autoplay
          const { channel, pjtvItem } = upNextProgram;
          
          setSelected({
            channelNumber: channel.number,
            title: pjtvItem.title,
            program: {
              title: pjtvItem.showTitle || pjtvItem.title,
              subtitle: pjtvItem.title,
              start: new Date(pjtvItem.start).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
              end: new Date(pjtvItem.end).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
              width: 1,
              rating: pjtvItem.rating || "TV-PG",
              genres: ["PJTV"],
              summary: pjtvItem.summary || "",
              pjtvItem,
            },
            programKey: `${channel.number}-${pjtvItem.ratingKey}`,
            locked: true,
            ratingKey: pjtvItem.ratingKey,
          });
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [upNextCountdown, upNextProgram]);

  const getPlutoChannelHexId = (channel) => {
    if (!channel) return null;
    const match = plutoStreams.find((s) => s.id === channel.plutoId);
    if (!match?.logo) return null;
    const hexMatch = match.logo.match(/channels\/([^/]+)\//);
    return hexMatch ? hexMatch[1] : null;
  };

  const selectedPlutoStream = (() => {
  const channel = selectedData.channel;
  if (!channel || channel.type !== "PLUTO") return null;

  const plutoStreamId =
    channel.plutoHexId ||
    channel.hex ||
    getPlutoChannelHexId(channel);

  const plutoMode = channel.plutoMode || channel.mode || "normal";
  const rawPlutoStream = channel.url || channel.streamUrl || channel.plutoUrl || null;

  if (plutoStreamId) {
    return (
      `/pluto-live/${plutoStreamId}` +
      (plutoMode === "livestitch" ? "?mode=livestitch" : "")
    );
  }

  if (channel.plutoId) return `/pluto-stream/${channel.plutoId}`;
  if (rawPlutoStream) return `/pluto-hls?url=${encodeURIComponent(rawPlutoStream)}`;
  return null;
})();

  const selectedTubiStream = (() => {
    const channel = selectedData.channel;

    if (!channel || channel.type !== "TUBI") return null;

    if (channel.tubiSlug && channel.tubiContentId) {
      return `/tubi-live/${channel.tubiSlug}?content_id=${channel.tubiContentId}`;
    }

    return null;
  })();

const livePJTVItem =
  selectedData.channel.type === "PJTV"
    ? getCurrentPJTVItem(selectedData.channel.number)
    : null;

const selectedPJTVItem =
  selectedData.channel.type === "PJTV"
    ? selected.locked
      ? selected.program?.pjtvItem || selectedData.program?.pjtvItem || null
      : selectedData.program?.pjtvItem || livePJTVItem
    : null;

const currentPJTVItem = selectedPJTVItem;
useEffect(() => {
  let cancelled = false;

  async function fetchTMDB() {
    const title =
      currentPJTVItem?.showTitle ||
      selectedData?.program?.showTitle ||
      selectedData?.program?.title ||
      currentPJTVItem?.title;

    const year = currentPJTVItem?.year;

    if (!title) {
      setTmdbData(null);
      return;
    }

    const data = await searchTMDBTV(title, year);

    if (!cancelled) {
      setTmdbData(data);
    }
  }

  fetchTMDB();

  return () => {
    cancelled = true;
  };
}, [
  currentPJTVItem?.showTitle,
  currentPJTVItem?.title,
  currentPJTVItem?.year,
  selectedData?.program?.showTitle,
  selectedData?.program?.title,
]);

const previewBackdrop =
  tmdbImage(tmdbData?.backdrop_path, "w1280") ||
  currentPJTVItem?.art ||
  selectedData.channel.preview;


const currentProgramProgress =
  currentPJTVItem?.kind === "episode"
    ? (() => {
        const start = new Date(currentPJTVItem.start).getTime();
        const end = new Date(currentPJTVItem.end).getTime();
        const duration = end - start;
        if (duration <= 0) return 0;
        const elapsed = Date.now() - start;
        return Math.min(100, Math.max(0, (elapsed / duration) * 100));
      })()
    : 0;

const activeStream = useMemo(() => {
  const channel = selectedData.channel;

  if (!channel) return null;

  const pjtvStreamUrl = (item, forceOffsetMs = null) => {
    if (!item?.ratingKey) return null;

    let offsetMs = Number(forceOffsetMs || 0);

    // If no stored offset was passed, calculate live offset only when currently airing.
    if (!offsetMs && item.start && item.end) {
      const now = Date.now();
      const start = new Date(item.start).getTime();
      const end = new Date(item.end).getTime();

      if (now >= start && now < end) {
        offsetMs = Math.max(0, now - start);
      }
    }

    return offsetMs > 0
      ? `/pjtv/play/${item.ratingKey}?offsetMs=${Math.floor(offsetMs)}`
      : `/pjtv/play/${item.ratingKey}`;
  };

  // PJTV: selected now-playing resumes live position; upcoming starts at beginning.
  if (channel.type === "PJTV") {
    if (selected.locked && selected.ratingKey) {
      return selected.offsetMs > 0
        ? `/pjtv/play/${selected.ratingKey}?offsetMs=${Math.floor(selected.offsetMs)}`
        : `/pjtv/play/${selected.ratingKey}`;
    }

    const lockedPJTVItem =
      selected.locked &&
      selectedData.program?.pjtvItem?.kind === "episode"
        ? selectedData.program.pjtvItem
        : null;

    if (lockedPJTVItem?.ratingKey) {
      return pjtvStreamUrl(lockedPJTVItem, selected.offsetMs);
    }

    const liveItem =
      currentPJTVItem?.kind === "episode"
        ? currentPJTVItem
        : null;

    if (liveItem?.ratingKey) {
      return pjtvStreamUrl(liveItem);
    }

    return channel.streamUrl || null;
  }

  if (channel.type === "PLUTO") {
    return selectedPlutoStream;
  }

  if (channel.type === "TUBI") {
    return selectedTubiStream;
  }

  if (channel.type === "DIZQUETV") {
    return channel.streamUrl || null;
  }

  return channel.streamUrl || null;
}, [
  selected,
  selectedData.channel,
  selectedData.program?.pjtvItem,
  currentPJTVItem,
  selectedPlutoStream,
  selectedTubiStream,
]);

const tvGuideMediaSession = useMemo(() => {
  if (!activeStream || !selectedData.channel) return null;

  const channel = selectedData.channel;
  const program = selectedData.program || {};
  const pjtvItem = program.pjtvItem || null;
  const channelName = channel.name || channel.guideLabel || channel.label || "";
  const channelNumber = channel.number ? String(channel.number) : "";
  const channelText = [channelNumber, channelName].filter(Boolean).join(" ");
  const subtitle = [channelName, channel.guideLabel]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" / ");

  return {
    source: "TV Guide",
    type: channel.type || "channel",
    title: program.title || channelName || "TV Guide",
    subtitle,
    channel: channelText || null,
    artwork:
      program.thumb ||
      pjtvItem?.thumb ||
      pjtvItem?.art ||
      channel.logo ||
      null,
    ratingKey: program.ratingKey || pjtvItem?.ratingKey || null,
    muted: !!videoRef.current?.muted,
    originPage: "tv",
    originRoute: "tv-guide",
  };
}, [activeStream, selectedData.channel, selectedData.program]);

useEffect(() => {
  if (!tvGuideMediaSession) return;

  setMediaBuffering(tvGuideMediaSession);
}, [tvGuideMediaSession]);

useEffect(() => {
  const video = videoRef.current;
  if (!video || !tvGuideMediaSession) return;

  const finiteDuration = () =>
    Number.isFinite(video.duration) ? video.duration : null;

  const publishPlaying = () => setMediaPlaying(tvGuideMediaSession);
  const publishPaused = () => setMediaPaused(tvGuideMediaSession);
  const publishBuffering = () => setMediaBuffering(tvGuideMediaSession);
  const publishProgress = () =>
    updateMediaProgress(video.currentTime || 0, finiteDuration());
  const publishMuted = () => setMediaMuted(video.muted);

  video.addEventListener("play", publishPlaying);
  video.addEventListener("pause", publishPaused);
  video.addEventListener("waiting", publishBuffering);
  video.addEventListener("loadstart", publishBuffering);
  video.addEventListener("timeupdate", publishProgress);
  video.addEventListener("volumechange", publishMuted);

  publishMuted();
  if (!video.paused) publishPlaying();

  return () => {
    video.removeEventListener("play", publishPlaying);
    video.removeEventListener("pause", publishPaused);
    video.removeEventListener("waiting", publishBuffering);
    video.removeEventListener("loadstart", publishBuffering);
    video.removeEventListener("timeupdate", publishProgress);
    video.removeEventListener("volumechange", publishMuted);
  };
}, [tvGuideMediaSession]);

const isPJTVBumper =
  !selected.locked &&
  selectedData.channel.type === "PJTV" &&
  currentPJTVItem?.kind === "bumper";

const bumperNext = currentPJTVItem?.next || null;

const bumperSecondsLeft = isPJTVBumper
  ? Math.max(
      0,
      Math.ceil(
        (new Date(currentPJTVItem.end).getTime() - Date.now()) / 1000
      )
    )
  : 0;


  useEffect(() => {
    setPreviewLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPreviewLoading(false), 180);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [selected]);

useEffect(() => {
  const video = videoRef.current;

  if (!video || !activeStream) {
    lastLoadedStreamRef.current = null;
    return;
  }

  if (lastLoadedStreamRef.current === activeStream) {
    return;
  }

  lastLoadedStreamRef.current = activeStream;

  if (video.hls) {
    video.hls.destroy();
    video.hls = null;
  }

  if (video.mpegtsPlayer) {
    video.mpegtsPlayer.destroy();
    video.mpegtsPlayer = null;
  }

  video.pause();
  video.removeAttribute("src");
  video.load();

  if (selectedData.channel.type === "DIZQUETV") {
    if (mpegts.getFeatureList().mseLivePlayback) {
      const player = mpegts.createPlayer({
        type: "mpegts",
        isLive: true,
        url: activeStream,
      });

      player.attachMediaElement(video);
      player.load();
      player.play().catch(() => {});
      video.mpegtsPlayer = player;

      return () => {
        player.destroy();
        video.mpegtsPlayer = null;
      };
    }

    return;
  }

  let hls;

  if (Hls.isSupported()) {
    hls = new Hls({
      startLevel: -1,
      capLevelToPlayerSize: true,
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 10,
      manifestLoadingMaxRetry: 6,
      manifestLoadingRetryDelay: 1000,
      levelLoadingMaxRetry: 6,
      levelLoadingRetryDelay: 1000,
      fragLoadingMaxRetry: 6,
    });

    video.hls = hls;
    hls.loadSource(activeStream);
    hls.attachMedia(video);

    let didApplyPJTVSeek = false;
    const seekSeconds = Number(selected.offsetMs || 0) / 1000;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (hls.levels?.length) hls.currentLevel = hls.levels.length - 1;

      // Start clean. Actual PJTV live seek happens after first fragment buffers.
      video.currentTime = 0;

      video.play().catch((err) => {
        console.warn("HLS play() failed:", err);
      });
    });

    hls.on(Hls.Events.FRAG_BUFFERED, () => {
      if (didApplyPJTVSeek || seekSeconds <= 0) return;

      didApplyPJTVSeek = true;

      try {
        video.currentTime = seekSeconds;
      } catch (err) {
        console.warn("PJTV seek after buffer failed:", err);
      }
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      console.warn("HLS ERROR:", data);

      if (!data.fatal) return;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
      } else {
        hls.destroy();
        video.hls = null;
        lastLoadedStreamRef.current = null;
      }
    });

    return () => {
      if (hls) hls.destroy();
      video.hls = null;
    };
  }

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = activeStream;
    video.currentTime = 0;
    video.play().catch(() => {});
  }
}, [activeStream, selectedData.channel.type]);

useEffect(() => {
  const video = videoRef.current;
  if (!video) return;

  const handleEnded = () => {if (selectedData.channel.type !== "PJTV") return;

    const channelNumber = String(selectedData.channel.number);
    const items = pjtvSchedule[channelNumber] || [];

    const currentRatingKey =
      selected.ratingKey ||
      selectedData.program?.pjtvItem?.ratingKey ||
      currentPJTVItem?.ratingKey;

    if (!currentRatingKey) {
      console.warn("PJTV ended: no current ratingKey found");
      return;
    }

    const currentIndex = items.findIndex(
      (item) => item.kind === "episode" && item.ratingKey === currentRatingKey
    );

    const nextItem = items
      .slice(currentIndex + 1)
      .find((item) => item.kind === "episode" && item.ratingKey);

    if (!nextItem) {
      console.warn("PJTV ended: no next episode found");
      return;
    }setSelected((prev) => ({
      ...prev,
      channelNumber: selectedData.channel.number,
      title: nextItem.showTitle || nextItem.title,
      program: {
        title: nextItem.showTitle || nextItem.title,
        subtitle: nextItem.title,
        start: new Date(nextItem.start).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        }),
        end: new Date(nextItem.end).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        }),
        rating: nextItem.rating || "TV-PG",
        genres: [nextItem.genres?.[0] || "TV"],
        summary: nextItem.summary || "",
        pjtvItem: nextItem,
      },
      programKey: `${selectedData.channel.number}-${nextItem.ratingKey}`,
      ratingKey: nextItem.ratingKey,
      locked: true,
    }));
  };

  video.addEventListener("ended", handleEnded);

  return () => {
    video.removeEventListener("ended", handleEnded);
  };
}, [
  selectedData.channel.type,
  selectedData.channel.number,
  selectedData.program,
  currentPJTVItem,
  selected.ratingKey,
  pjtvSchedule,
]);

  const jumpGuide = (dir) => {
    if (!guideRef.current) return;
    guideRef.current.scrollBy({ left: dir * SLOT_WIDTH * 2, behavior: "smooth" });
  };

  const getGuideWindowStartMs = () => {
    const base = new Date();
    base.setMinutes(base.getMinutes() - (base.getMinutes() % 30), 0, 0);
    return base.getTime();
  };

  const getProgramTimeRange = (program, offsetUnits, unitMinutes) => {
    const pjtvStart = new Date(program?.pjtvItem?.start).getTime();
    const pjtvEnd = new Date(program?.pjtvItem?.end).getTime();

    if (
      Number.isFinite(pjtvStart) &&
      Number.isFinite(pjtvEnd) &&
      pjtvEnd > pjtvStart
    ) {
      return { startMs: pjtvStart, endMs: pjtvEnd };
    }

    const width = Math.max(1, Number(program?.width) || 1);
    const unitMs = unitMinutes * 60 * 1000;
    const startMs = getGuideWindowStartMs() + offsetUnits * unitMs;

    return {
      startMs,
      endMs: startMs + width * unitMs,
    };
  };

  useEffect(() => {
    const gridCellSelector = ".tvguide-channel-cell, .tvguide-program-cell";
    const ownedSelector = [
      ".tvguide-back-btn",
      ".tvguide-channel-cell",
      ".tvguide-program-cell",
      ".tvguide-player-control"
    ].join(",");

    const getFocusedOwnedEl = () => {
      const candidates = [
        document.activeElement,
        document.querySelector(".napflix-tv-focused")
      ];

      for (const candidate of candidates) {
        const el = candidate?.matches?.(ownedSelector)
          ? candidate
          : candidate?.closest?.(ownedSelector);

        if (el?.isConnected) {
          return el;
        }
      }

      return null;
    };

    const ensureGridCellVisible = (cell, scrollLeft) => {
      if (!cell || !guideRef.current?.contains(cell)) return;

      const guide = guideRef.current;
      const guideRect = guide.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();

      if (cellRect.top < guideRect.top) {
        guide.scrollTop -= guideRect.top - cellRect.top;
      } else if (cellRect.bottom > guideRect.bottom) {
        guide.scrollTop += cellRect.bottom - guideRect.bottom;
      }

      guide.scrollLeft = scrollLeft;
    };

    const focusOwnedEl = (el, preserveScroll = false) => {
      if (!el) return false;

      const scrollLeft = guideRef.current?.scrollLeft || 0;

      document
        .querySelectorAll(".napflix-tv-focused")
        .forEach((el) => el.classList.remove("napflix-tv-focused"));

      el.classList.add("napflix-tv-focused");
      el.focus({ preventScroll: true });
      window.__napflixCurrentFocus = el;

      if (el.matches(gridCellSelector)) {
        lastGuideFocusRef.current = el;
      }

      if (preserveScroll && guideRef.current) {
        guideRef.current.scrollLeft = scrollLeft;
        ensureGridCellVisible(el, scrollLeft);
        requestAnimationFrame(() => {
          if (guideRef.current) {
            guideRef.current.scrollLeft = scrollLeft;
            ensureGridCellVisible(el, scrollLeft);
          }
        });
      }

      return true;
    };

    const focusGridCell = (cell, preserveScroll = false) =>
      focusOwnedEl(cell, preserveScroll);

    const cellsInRow = (row) =>
      Array.from(guideRef.current?.querySelectorAll(gridCellSelector) || [])
        .filter((cell) => Number(cell.dataset.tvguideRow) === row)
        .sort((a, b) => Number(a.dataset.tvguideCol) - Number(b.dataset.tvguideCol));

    const rowNumbers = () =>
      Array.from(guideRef.current?.querySelectorAll(gridCellSelector) || [])
        .map((cell) => Number(cell.dataset.tvguideRow))
        .filter(Number.isFinite)
        .filter((row, index, rows) => rows.indexOf(row) === index)
        .sort((a, b) => a - b);

    const adjacentRow = (row, dir) => {
      const rows = rowNumbers();
      const index = rows.indexOf(row);

      if (index === -1) return null;
      return rows[index + dir] ?? null;
    };

    const visibleGridCells = () => {
      const guideRect = guideRef.current?.getBoundingClientRect();

      return Array.from(guideRef.current?.querySelectorAll(gridCellSelector) || [])
        .filter((cell) => {
          const r = cell.getBoundingClientRect();
          return (
            r.width > 20 &&
            r.height > 20 &&
            (!guideRect || (r.bottom > guideRect.top && r.top < guideRect.bottom))
          );
        })
        .sort((a, b) => {
          const rowDiff = Number(a.dataset.tvguideRow) - Number(b.dataset.tvguideRow);
          if (rowDiff) return rowDiff;
          return Number(a.dataset.tvguideCol) - Number(b.dataset.tvguideCol);
        });
    };

    const firstVisibleGridCell = () =>
      visibleGridCells().find((cell) => cell.classList.contains("tvguide-channel-cell")) ||
      visibleGridCells()[0] ||
      null;

    const firstVisibleRow = () => {
      const rows = visibleGridCells()
        .map((cell) => Number(cell.dataset.tvguideRow))
        .filter(Number.isFinite);

      return rows.length ? Math.min(...rows) : 0;
    };

    const channelInRow = (row) =>
      cellsInRow(row).find((cell) => cell.classList.contains("tvguide-channel-cell")) || null;

    const verticalAnchorX = (active) => {
      if (!guideRef.current) return null;

      const guideRect = guideRef.current.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const timelineLeft = guideRect.left + CHANNEL_COL_WIDTH;
      const timelineRight = guideRect.right;
      const visibleTimelineWidth = Math.max(0, timelineRight - timelineLeft);
      const wideCell = activeRect.width >= visibleTimelineWidth * 0.85;
      const preferredX = wideCell ? activeRect.left : activeRect.left + activeRect.width / 2;

      return Math.min(timelineRight, Math.max(timelineLeft, preferredX));
    };

    const programInRowByTime = (row, active) => {
      const programs = cellsInRow(row).filter((cell) =>
        cell.classList.contains("tvguide-program-cell")
      );
      const anchorX = verticalAnchorX(active);

      if (!Number.isFinite(anchorX)) return programs[0] || null;

      let containing = null;
      let bestNearest = null;

      programs.forEach((cell) => {
        const rect = cell.getBoundingClientRect();

        if (anchorX >= rect.left && anchorX < rect.right && !containing) {
          containing = cell;
        }

        const distance = Math.abs(rect.left + rect.width / 2 - anchorX);

        if (!bestNearest || distance < bestNearest.distance) {
          bestNearest = { cell, distance };
        }
      });

      return containing || bestNearest?.cell || programs[0] || null;
    };

    const moveVertical = (active, dir) => {
      const row = Number(active.dataset.tvguideRow);

      if (!Number.isFinite(row)) return false;

      if (dir < 0 && row <= firstVisibleRow()) {
        return focusOwnedEl(document.querySelector(".tvguide-back-btn"));
      }

      const nextRow = adjacentRow(row, dir);
      if (nextRow == null) return false;

      if (active.classList.contains("tvguide-channel-cell")) {
        return focusGridCell(channelInRow(nextRow), true);
      }

      return focusGridCell(programInRowByTime(nextRow, active), true);
    };

    const playerControls = () =>
      Array.from(document.querySelectorAll(".tvguide-player-control"))
        .filter((el) => {
          const r = el.getBoundingClientRect();
          const st = window.getComputedStyle(el);
          return (
            r.width > 20 &&
            r.height > 20 &&
            st.display !== "none" &&
            st.visibility !== "hidden"
          );
        });

    const movePlayerControl = (active, dir) => {
      const controls = playerControls();
      const index = controls.indexOf(active);

      if (index === -1) return false;
      return focusOwnedEl(controls[index + dir]);
    };

    const moveHorizontal = (active, dir) => {
      const row = Number(active.dataset.tvguideRow);
      if (!Number.isFinite(row)) return false;

      const cells = cellsInRow(row);
      const index = cells.indexOf(active);
      if (index === -1) return false;

      return focusGridCell(cells[index + dir]);
    };

    const onKey = (e) => {
      const active = getFocusedOwnedEl();
      if (!active) return;

      if (active.classList.contains("tvguide-back-btn")) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          focusGridCell(firstVisibleGridCell(), true);
          return;
        }

        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          active.click();
        }

        return;
      }

      if (active.classList.contains("tvguide-player-control")) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          movePlayerControl(active, -1);
          return;
        }

        if (e.key === "ArrowRight") {
          e.preventDefault();
          movePlayerControl(active, 1);
          return;
        }

        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          if (e.key === "ArrowDown") {
            focusGridCell(lastGuideFocusRef.current, true);
          }
          return;
        }

        if (e.key === "Escape" || e.key === "Backspace") {
          e.preventDefault();
          focusGridCell(lastGuideFocusRef.current, true);
          return;
        }

        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          active.click();
        }

        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        moveVertical(active, -1);
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveVertical(active, 1);
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveHorizontal(active, -1);
        return;
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        moveHorizontal(active, 1);
        return;
      }

      if (
        active.classList.contains("tvguide-program-cell") &&
        (e.key === "Enter" || e.key === " " || e.key === "Spacebar")
      ) {
        e.preventDefault();
        lastGuideFocusRef.current = active;
        active.click();
        setTimeout(() => focusOwnedEl(playerControls()[0]), 80);
      }
    };

    const onFocusChannel = (event) => {
      const channelNumber = event.detail?.channelNumber;
      if (channelNumber == null) return;

      const cell = Array.from(
        guideRef.current?.querySelectorAll(
          ".tvguide-channel-cell[data-tvguide-channel-number]"
        ) || []
      ).find(
        (candidate) =>
          String(candidate.dataset.tvguideChannelNumber) === String(channelNumber)
      );

      if (cell) {
        focusGridCell(cell, true);
      }
    };

    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pjtv:focusChannel", onFocusChannel);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pjtv:focusChannel", onFocusChannel);
    };
  }, []);

  return (
    <div style={styles.root}>
      <style>{`
        @keyframes pj-tv-spin { to { transform: rotate(360deg); } }
        @keyframes pj-flicker { 0%,100%{opacity:.06} 50%{opacity:.12} }

        .guideScroll::-webkit-scrollbar {
          width: 20px;
          height: 20px;
        }

        .guideScroll::-webkit-scrollbar-track {
          background: #050e22;
        }

        .guideScroll::-webkit-scrollbar-thumb {
          background: #d7c343;
          border-radius: 0;
          border: 3px solid #050e22;
        }

        .guideScroll::-webkit-scrollbar-thumb:hover {
          background: #f3df58;
        }

        .guideScroll::-webkit-scrollbar-corner {
          background: #050e22;
        }

        .tvguide-side-controls {
          min-height: 96px !important;
          height: 96px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 18px !important;
          padding: 10px 0 0 !important;
          background: transparent !important;
          border: 0 !important;
          border-radius: 0 !important;
        }

        .tvguide-side-controls .tvguide-player-control {
          width: 64px !important;
          height: 64px !important;
          min-width: 64px !important;
          min-height: 64px !important;
          border-radius: 50% !important;
          font-size: 28px !important;
          background: rgba(8, 16, 32, 0.58) !important;
          border: 1px solid rgba(255,255,255,0.28) !important;
          color: white !important;
          backdrop-filter: blur(14px) saturate(1.35) !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.18),
            0 12px 32px rgba(0,0,0,0.42),
            0 0 18px rgba(229,160,13,0.12) !important;
        }

        .tvguide-side-controls .tvguide-player-control.napflix-tv-focused,
        .tvguide-side-controls .tvguide-player-control:focus {
          background: rgba(229,160,13,0.22) !important;
          border-color: rgba(255,220,105,0.92) !important;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.24),
            0 0 0 3px rgba(255,255,255,0.82),
            0 0 0 7px rgba(229,160,13,0.95),
            0 0 34px rgba(229,160,13,0.78) !important;
          transform: scale(1.08) !important;
          z-index: 999999 !important;
        }

      `}</style>

      <div style={styles.shell}>

        {/* ── TOP: preview + info ── */}
        <div style={styles.topArea}>

          {/* Preview */}
          <div style={styles.previewSide}>
            <div
  className={`tvguide-preview-box${guidePlayerMode ? " player-mode" : ""}${previewFull ? " full" : ""}`}
  tabIndex={-1}
  style={styles.previewBox}
  onFocus={() => setGuidePlayerMode(true)}
  onClick={() => setGuidePlayerMode(true)}
>

              <button
                className="tvguide-back-btn"
                style={styles.backBtn}
                onClick={() => {
                  window.location.hash = "";
                  window.dispatchEvent(new CustomEvent("pj:setPage", { detail: "home" }));
                }}
              >
                ← GUIDE
              </button>

{selectedData.channel.type === "STATIC" ? (
  <div
    style={{
      ...styles.previewImage,
      backgroundImage: `url(${previewBackdrop})`,
      opacity: previewLoading ? 0.28 : 1,
    }}
  />
  
) : isPJTVBumper ? (
  <div style={styles.nextUpScreen}>
    {(() => {
      const nextUpImage = bumperNext?.thumb || bumperNext?.art || bumperNext?.grandparentThumb || null;
      return nextUpImage ? (
        <img loading="lazy" decoding="async" src={nextUpImage} alt="" style={styles.nextUpImage} />
      ) : null;
    })()}

    <div style={styles.nextUpOverlay}>
      <div style={styles.nextUpEyebrow}>NEXT UP</div>
      <div style={styles.nextUpShow}>{bumperNext?.showTitle}</div>
      <div style={styles.nextUpEpisode}>{bumperNext?.title}</div>

      <div style={styles.nextUpMeta}>
        {bumperNext?.year} · {bumperNext?.rating}
        {bumperNext?.season && bumperNext?.episode
          ? ` · S${String(bumperNext.season).padStart(2, "0")}E${String(
              bumperNext.episode
            ).padStart(2, "0")}`
          : ""}
      </div>

      <div style={styles.nextUpSummary}>
        {bumperNext?.summary}
      </div>

      <div style={styles.nextUpCountdown}>
        Starts in {Math.floor(bumperSecondsLeft / 60)}:
        {String(bumperSecondsLeft % 60).padStart(2, "0")}
      </div>
    </div>
  </div>
) : activeStream ? (
  <>
    <video
  ref={videoRef}
  className="tvguide-preview-video"
  tabIndex={-1}
  autoPlay
  muted={previewMuted}
  playsInline
  disablePictureInPicture
  disableRemotePlayback
  controlsList="nodownload noplaybackrate noremoteplayback"
  preload="auto"
  style={styles.previewVideo}
  onFocus={(e) => e.currentTarget.blur()}
  onContextMenu={(e) => e.preventDefault()}
  onLoadedMetadata={(e) => {
  try {
    if (
      selectedData.channel.type === "PJTV" &&
      currentPJTVItem?.kind === "episode" &&
      !selected.locked
    ) {
      const start = new Date(currentPJTVItem.start).getTime();
      const end = new Date(currentPJTVItem.end).getTime();
      const now = Date.now();

      if (now >= start && now < end) {
        const offset = Math.max(0, (now - start) / 1000);
        e.target.currentTime = offset;
      }
    } else {
      e.target.currentTime = 0;
    }
  } catch (err) {
    console.warn("PJTV seek failed:", err);
  }
}}
/>

  </>
) : (
  <div
    style={{
      ...styles.previewImage,
      backgroundImage: `url(${previewBackdrop})`,
      opacity: previewLoading ? 0.28 : 1,
    }}
  />
)}
              <div style={styles.previewOverlay} />
              <div style={styles.scanlines} />
              <div style={styles.previewBug}>TV</div>
              {selectedData.channel.type === "STATIC" && (
                <div style={styles.staticBadge}>PREVIEW ONLY</div>
              )}
              {previewLoading && (
                <div style={styles.loadingWrap}>
                  <div style={styles.spinner} />
                  <div style={styles.loadingText}>TUNING CHANNEL...</div>
                </div>
              )}
            </div>
          </div>

          {/* Info panel */}
          <div style={styles.infoSide}>
            <div style={styles.infoKicker}>
              {selectedData.channel.number} {selectedData.channel.name}
            </div>
            <div style={styles.infoSub}>{selectedData.channel.category}</div>
            <div style={styles.infoTitle}>{selectedData.program?.title || "No Program"}</div>
            <div style={styles.infoTime}>
              {selectedData.program?.start || "--"} — {selectedData.program?.end || "--"}
            </div>
            <div style={styles.badgeRow}>
  <span style={styles.rating}>{selectedData.program?.rating || "TV-PG"}</span>
  {(selectedData.program?.genres || []).map((g) => (
    <span key={g} style={styles.genre}>{g}</span>
  ))}
</div>

{selectedData.program?.pjtvItem && (
  <div style={styles.episodeMeta}>
    <div style={styles.episodeYear}>
      {selectedData.program.pjtvItem.year || ""}
    </div>

    <div style={styles.episodeSeasonEp}>
      {selectedData.program.pjtvItem.season && selectedData.program.pjtvItem.episode
        ? `Season ${selectedData.program.pjtvItem.season}  |  Episode ${selectedData.program.pjtvItem.episode}`
        : ""}
    </div>

    {(selectedData.program?.subtitle || selectedData.program?.pjtvItem?.title) && (
      <div style={styles.episodeTitle}>
        {selectedData.program.subtitle || selectedData.program.pjtvItem.title}
      </div>
    )}
  </div>
)}

{/* Non-PJTV or fallback subtitle */}
{!selectedData.program?.pjtvItem && selectedData.program?.subtitle && (
  <div style={styles.episodeMeta}>
    {selectedData.program.subtitle}
    {selectedData.program?.pjtvItem?.season && selectedData.program?.pjtvItem?.episode
      ? ` · S${String(selectedData.program.pjtvItem.season).padStart(2, "0")}E${String(
          selectedData.program.pjtvItem.episode
        ).padStart(2, "0")}`
      : ""}
    {selectedData.program?.pjtvItem?.year
      ? ` · ${selectedData.program.pjtvItem.year}`
      : ""}
  </div>
)}

{selectedData.channel.type === "PJTV" && currentPJTVItem?.kind === "episode" && (
  <div style={styles.progressTrack}>
    <div
      style={{
        ...styles.progressFill,
        width: `${currentProgramProgress}%`,
      }}
    />
  </div>
)}

<div style={styles.rule} />
            <div style={styles.summaryWrap}>
              <div style={styles.summary}>
                {selectedData.program?.summary || "No program information available."}
              </div>
              <div style={styles.summaryFade} />
            </div>

            {/* Footer — TV Guide player controls */}
            <div className="tvguide-side-controls">
              <button
                className="tvguide-player-control"
                onClick={() => {
                  const v = videoRef.current;
                  if (!v) return;
                  v.paused ? v.play() : v.pause();
                }}
              >
                ⏯
              </button>

              <button
                className="tvguide-player-control"
                onClick={() => {
                  const v = videoRef.current;
                  if (v) v.muted = !v.muted;
                  setPreviewMuted((m) => !m);
                }}
              >
                {previewMuted ? "🔊" : "🔇"}
              </button>

              <button
                className="tvguide-player-control"
                onClick={async () => {
                  const target =
                    document.querySelector(".tvguide-preview-box") ||
                    videoRef.current;

                  try {
                    if (document.fullscreenElement) {
                      await document.exitFullscreen?.();
                      setPreviewFull(false);
                      return;
                    }

                    if (target?.requestFullscreen) {
                      await target.requestFullscreen();
                      setPreviewFull(true);
                      return;
                    }
                  } catch (err) {
                    console.warn("TV Guide fullscreen failed:", err);
                  }

                  setPreviewFull((f) => !f);
                }}
              >
                {previewFull ? "▣" : "⛶"}
              </button>
            </div>
         </div>
        </div>

        {/* ── TICKER ── */}
        <div style={styles.tickerRow}>
          <div style={styles.timeStamp}>{displayTime}</div>
          <div style={styles.ticker}>
            {selectedData.channel.number} &nbsp;·&nbsp; {selectedData.channel.name} &nbsp;·&nbsp; {selectedData.program?.title || "No Program"}
          </div>
        </div>

        {/* ── GUIDE GRID ── */}
        <div style={styles.bottomArea}>
          <button
  className="tvguide-edge-btn"
  data-tvguide-focus="edge"
  data-tvguide-row={0}
  data-tvguide-col={-2}
  tabIndex={0}
  style={styles.edgeBtn}
  onClick={() => jumpGuide(-1)}
>
  ‹
</button>

          <div ref={guideRef} className="guideScroll" style={styles.guideScroller}>
            {/* Time header */}
            <div
              style={{
                ...styles.timeHeader,
                gridTemplateColumns: `${CHANNEL_COL_WIDTH}px repeat(${TIME_SLOTS.length}, ${SLOT_WIDTH}px)`,
              }}
            >
              <div style={styles.genreHeader}>CHANNEL</div>
              {liveTimeSlots.map((t, i) => (
                <div
                  key={`${t}-${i}`}
                  style={{
                    ...styles.timeCell,
                    color: i === 0 ? "#ff4444" : "#d7c343",
                  }}
                >
                  {t}
                </div>
              ))}
            </div>

            {/* Channel rows */}
            <div style={styles.rows}>
              {guideChannels.map((channel, index) => {
                const previousChannel = guideChannels[index - 1];
                const showGroupSeparator =
                  index === 0 || previousChannel?.group !== channel.group;

                const config = CONFIG_BY_NUMBER[String(channel.number)];
                const registry = CHANNEL_REGISTRY[String(channel.number)] || {};
                
                const mergedChannel = {
                  ...channel,
                  ...config,
                  ...registry,
               };

                const channelDisplayName =
                  mergedChannel.displayName ||
                  mergedChannel.network ||
                  mergedChannel.name;

                const channelDisplayCategory =
                  mergedChannel.category || "";

                const channelLogo = getChannelLogo(mergedChannel);


                return (
  <React.Fragment key={mergedChannel.number}>
    {showGroupSeparator && (
      <div style={styles.groupSeparator}>
        <span style={styles.groupSeparatorText}>
          {channel.group}
        </span>
      </div>
    )}

    <div
      style={{
        ...styles.row,
        gridTemplateColumns:
          channel.type === "PJTV"
            ? `${CHANNEL_COL_WIDTH}px repeat(${TIME_SLOTS.length * 6}, ${SLOT_WIDTH / 6}px)`
            : `${CHANNEL_COL_WIDTH}px repeat(${TIME_SLOTS.length}, ${SLOT_WIDTH}px)`,
      }}
    >
<button
  className="tvguide-channel-cell"
  data-tvguide-focus="channel"
  data-tvguide-row={index}
  data-tvguide-col={-1}
  data-tvguide-channel-number={channel.number}
  tabIndex={0}
  style={styles.channelCell}
  onClick={() => {
    const programs =
      channel.type === "PJTV"
        ? getPJTVDisplayPrograms(channel)
        : channel.programs || [];

    const firstProgram = programs[0];

    if (!firstProgram) return;

    const firstKey =
      channel.type === "PJTV" && firstProgram?.pjtvItem?.ratingKey
        ? `${channel.number}-${firstProgram.pjtvItem.ratingKey}`
        : `${channel.number}-${firstProgram.start}-${firstProgram.end}-${firstProgram.title}`;

    setSelected({
      channelNumber: channel.number,
      title: firstProgram.title,
      program: firstProgram,
      programKey: firstKey,
      locked: channel.type === "PJTV" && firstProgram?.pjtvItem?.kind === "episode",
      ratingKey: firstProgram?.pjtvItem?.ratingKey,
      offsetMs: 0,
    });
  }}
>
  <div style={styles.channelHeader}>
    {channelLogo && (
      <div style={styles.channelLogoBox}>
        <img
        decoding="async"
          src={channelLogo}
          alt={channelDisplayName}
          style={styles.channelLogoImg}
          onLoad={(e) => {
            const img = e.currentTarget;
            const ratio = img.naturalWidth / img.naturalHeight;

            let scale = 1;

            // Very wide logos often feel too small vertically
            if (ratio > 3.5) scale = 1.28;

            // Moderately wide logos
            else if (ratio > 2.5) scale = 1.15;

            // Tall/narrow logos
            else if (ratio < 1.2) scale = 0.9;

            img.style.transform = `scale(${scale})`;
          }}
       />
      </div>
    )}

    <div style={styles.channelNum}>{mergedChannel.number}</div>
  </div>
</button>

                    {(() => {
                      const rowPrograms =
                        channel.type === "PJTV"
                          ? getPJTVDisplayPrograms(channel)
                          : channel.programs || [];
                      const unitMinutes = channel.type === "PJTV" ? 5 : 30;
                      let programOffsetUnits = 0;

                      return rowPrograms.map((program, idx) => {
                        const programTimeRange = getProgramTimeRange(
                          program,
                          programOffsetUnits,
                          unitMinutes
                        );

                        programOffsetUnits += Math.max(1, Number(program?.width) || 1);

                      const programKey =
                        channel.type === "PJTV" && program?.pjtvItem?.ratingKey
                          ? `${channel.number}-${program.pjtvItem.ratingKey}`
                          : `${channel.number}-${program.start}-${program.end}-${program.title}`;

                      const isSelected =
                        selected.channelNumber === channel.number &&
                        selected.programKey === programKey;

                      const category = channel.category?.toLowerCase() || "";

                      // Determine if this should be a locked selection
                      const shouldLock = 
                        channel.type === "PJTV" && 
                        program?.pjtvItem?.kind === "episode";

                      return (
                        <button
  key={`${channel.number}-${idx}`}
  className="tvguide-program-cell"
  data-tvguide-focus="program"
  data-tvguide-row={index}
  data-tvguide-col={idx}
  data-tvguide-start-ms={programTimeRange.startMs}
  data-tvguide-end-ms={programTimeRange.endMs}
  tabIndex={0}
  onClick={() => {
                            const isEpisode =
                              channel.type === "PJTV" &&
                              program?.pjtvItem?.kind === "episode";

                            const now = Date.now();
                            const startMs = isEpisode ? new Date(program.pjtvItem.start).getTime() : 0;
                            const endMs = isEpisode ? new Date(program.pjtvItem.end).getTime() : 0;
                            const isNowPlaying =
                              isEpisode &&
                              Number.isFinite(startMs) &&
                              Number.isFinite(endMs) &&
                              now >= startMs &&
                              now < endMs;

                            setSelected({
                              channelNumber: channel.number,
                              title: program.title,
                              program,
                              programKey,
                              locked: isEpisode,
                              ratingKey: isEpisode ? program.pjtvItem.ratingKey : undefined,
                              offsetMs: isNowPlaying ? Math.max(0, now - startMs) : 0,
                            });
                         }}

                          style={{
                            ...styles.programCell,
                            gridColumn: `span ${program.width}`,
                            boxShadow: isSelected
                              ? "inset 0 0 0 3px #d7c343"
                              : "none",
                            background: isSelected
                              ? "#0d2040"
                              : category.includes("news")
                              ? "#08263a"
                              : category.includes("movie")
                              ? "#1b1433"
                              : category.includes("sports")
                              ? "#132b18"
                              : "#091628",
                            transition: "background 0.2s ease, box-shadow 0.2s ease",
                          }}
                        >
                          <div style={styles.programTitle}>{program.title}</div>
                          {program.subtitle && (
                            <div style={styles.programSubtitle}>{program.subtitle}</div>
                          )}
                        </button>
                      );
                      });
                    })()}
                  </div>
                </React.Fragment>
              );
            })}
            </div>
          </div>

          <button
  className="tvguide-edge-btn"
  data-tvguide-focus="edge"
  data-tvguide-row={0}
  data-tvguide-col={999}
  tabIndex={0}
  style={styles.edgeBtn}
  onClick={() => jumpGuide(1)}
>
  ›
</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: "fixed",
    inset: 0,
    zIndex: 1200,
    background: "#000",
    color: "#fff",
    padding: "10px",
    overflow: "hidden",
    fontFamily: "'Bebas Neue', 'Arial Narrow', sans-serif",
  },
  shell: {
    width: "100%",
    height: "100%",
    display: "grid",
    gridTemplateRows: "52vh 28px 1fr",
    background: "#000",
    border: "2px solid #1a2a5a",
    overflow: "hidden",
  },
  topArea: {
    display: "grid",
    gridTemplateColumns: "2.6fr 0.65fr",
    minHeight: 0,
    borderBottom: "2px solid #1a2a5a",
  },
  previewSide: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    background: "#000",
  },
  previewBox: {
    position: "relative",
    flex: 1,
    minHeight: 0,
    background: "#000",
    overflow: "hidden",
  },
  previewImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    backgroundSize: "contain",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#000",
    zIndex: 1,
  },
  previewVideo: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    background: "#000",
    zIndex: 2,
  },
  previewOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 100%)",
    zIndex: 3,
    pointerEvents: "none",
  },
  scanlines: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 4,
    background:
      "repeating-linear-gradient(to bottom, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 1px, transparent 2px, transparent 4px)",
    animation: "pj-flicker 2.4s infinite",
  },
  previewBug: {
    position: "absolute",
    top: 10,
    right: 10,
    background: "rgba(0,0,0,0.75)",
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "3px 8px",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 1,
    zIndex: 5,
    color: "#fff",
  },
  backBtn: {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 10,
    background: "rgba(0,0,0,0.78)",
    border: "1px solid rgba(122,159,255,0.45)",
    color: "#c0d4ee",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 1.4,
    padding: "5px 11px",
    cursor: "pointer",
    textTransform: "uppercase",
    fontFamily: "'Bebas Neue', 'Arial Narrow', sans-serif",
  },
  loadingWrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    background: "rgba(0,0,0,0.5)",
    zIndex: 6,
  },
  spinner: {
    width: 24,
    height: 24,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.15)",
    borderTopColor: "#5b8cff",
    animation: "pj-tv-spin .8s linear infinite",
  },
  loadingText: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 2,
    color: "rgba(255,255,255,0.6)",
  },
  infoSide: {
  padding: "14px 16px 12px 16px",
  borderLeft: "2px solid #1a2a5a",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: 0,
  background: "#050d1a",
},
  infoKicker: {
  fontSize: 12,
  fontWeight: 900,
  color: "#5d86d6",
  letterSpacing: 0.8,
  textTransform: "uppercase",
  marginBottom: 4,
},

infoSub: {
  fontSize: 11,
  fontWeight: 800,
  color: "#7ea0d8",
  letterSpacing: 2,
  textTransform: "uppercase",
  marginBottom: 10,
},
  infoTitle: {
    fontSize: 26,
    lineHeight: 1.05,
    fontWeight: 900,
    marginBottom: 8,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoTime: {
    fontSize: 18,
    fontWeight: 900,
    marginBottom: 10,
    color: "#d7c343",
    letterSpacing: 0.5,
  },
  badgeRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 10,
  },
  rating: {
    background: "#0a1a3a",
    color: "#7a9fff",
    padding: "2px 7px",
    border: "1px solid #1e3a7a",
    fontWeight: 900,
    fontSize: 10,
    letterSpacing: 0.5,
  },
  genre: {
    fontSize: 14,
    fontWeight: 700,
    color: "#4a6a9a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  rule: {
    height: 1,
    background: "#1a2a5a",
    marginBottom: 10,
  },
  episodeMeta: {
    marginTop: 10,
    marginBottom: 12,
    color: "#d7c343",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: "uppercase",
  },
  episodeYear: {
    color: "#5a7ab0",
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
  },
  episodeSeasonEp: {
    color: "#d7c343",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  episodeTitle: {
    marginTop: 6,
    fontSize: 18,
    fontWeight: 900,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  episodeTitle: {
    marginTop: 8,
    color: "#f0f4ff",
    fontSize: 20,
    fontWeight: 900,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    lineHeight: 1.1,
  },
  progressTrack: {
    width: "100%",
    height: 6,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(215,195,67,0.35)",
    margin: "12px 0 18px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#d7c343",
  },
  summaryWrap: {
  position: "relative",
  minHeight: 0,
  flex: "1 1 auto",
  overflow: "hidden",
  marginBottom: 12,
},

summary: {
  fontFamily: "Inter, Arial, sans-serif",
  fontSize: 14,
  lineHeight: 1.55,
  color: "#aebfda",
  overflowY: "auto",
  maxHeight: "100%",
  paddingRight: 6,
},
  controls: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: "#020810",
    borderTop: "1px solid #1a2a5a",
    gap: 8,
  },
  power: {
    background: "#1a0505",
    color: "#cc2233",
    border: "1px solid #3a0a0a",
    fontWeight: 900,
    fontSize: 10,
    cursor: "pointer",
    padding: "4px 10px",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  footerLogo: {
    height: 75,
    width: "auto",
    objectFit: "contain",
    opacity: 0.85,
  },
  pjLogoBox: {
  marginTop: "auto",
  background: "#000",
  borderTop: "1px solid #1a2a5a",
  borderLeft: "0",
  borderRight: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  minHeight: 120,
  flexShrink: 0,
},

pjLogo: {
  maxWidth: "86%",
  maxHeight: 82,
  objectFit: "contain",
},
  tickerRow: {
    display: "grid",
    gridTemplateColumns: "100px 1fr",
    borderTop: "1px solid #0a1428",
    borderBottom: "1px solid #0a1428",
    background: "#040c1e",
  },
  timeStamp: {
    background: "#081428",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    fontWeight: 100,
    color: "#d7c343",
    borderRight: "1px solid #0a1a3a",
    letterSpacing: 1,
    fontFamily: "'Bebas Neue', 'Arial Narrow', sans-serif",
  },
  ticker: {
    background: "#040c1e",
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    fontSize: 11,
    fontWeight: 100,
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    letterSpacing: 0.5,
    fontFamily: "'Bebas Neue', 'Arial Narrow', sans-serif",
  },
  bottomArea: {
    display: "grid",
    gridTemplateColumns: "22px 1fr 22px",
    minHeight: 0,
    overflow: "hidden",
  },
  edgeBtn: {
    background: "#040c1e",
    color: "#2a4a8a",
    border: "none",
    fontSize: 20,
    fontWeight: 900,
    cursor: "pointer",
    borderRight: "1px solid #0a1428",
    borderLeft: "1px solid #0a1428",
  },
  guideScroller: {
    overflow: "auto",
    background: "#000",
  },
  timeHeader: {
    display: "grid",
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#040c1e",
    borderBottom: "2px solid rgba(255,255,255,0.15)",
  },
  genreHeader: {
    background: "#081428",
    color: "#4a6aaa",
    fontSize: 14,
    fontWeight: 400,
    padding: "7px 6px",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  timeCell: {
    padding: "7px 6px",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    fontSize: 20,
    fontWeight: 900,
    background: "#040c1e",
    letterSpacing: 0.5,
  },
  rows: {
    display: "flex",
    flexDirection: "column",
  },
  groupSeparator: {
    gridColumn: "1 / -1",
    height: 34,
    display: "flex",
    alignItems: "center",
    padding: "0 14px",
    marginTop: 8,
    marginBottom: 4,
    background: "linear-gradient(90deg, rgba(215,195,67,0.28), rgba(215,195,67,0.04))",
    borderTop: "1px solid rgba(215,195,67,0.35)",
    borderBottom: "1px solid rgba(0,0,0,0.45)",
    color: "#f4df6a",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: "uppercase",
  },
  groupSeparatorText: {
    textShadow: "0 1px 2px rgba(0,0,0,0.8)",
  },
  row: {
    display: "grid",
    minHeight: 92,
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  },
  channelCell: {
  background: "linear-gradient(180deg, #0b1f3f 0%, #06142a 100%)",
  color: "#fff",
  borderRight: "3px solid #1f3a7a",
  padding: "10px 18px 10px 12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 10,
  minWidth: 130,
  boxShadow: "inset -2px 0 0 rgba(255,255,255,0.06), 2px 0 8px rgba(0,0,0,0.4)",
},
channelHeader: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: 10,
},
channelLogoBox: {
  width: 72,
  height: 42,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "visible",
  flexShrink: 0,
},

channelLogoImg: {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  filter: "brightness(1.15) contrast(1.08)",
  transformOrigin: "center center",
},

channelNum: {
  fontSize: 28,
  fontWeight: 900,
  color: "#d7c343",
  width: 54,
  textAlign: "right",
  marginLeft: "auto",
  paddingRight: 8,
  lineHeight: 1,
  flexShrink: 0,
},
  channelMain: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: 1.1,
    letterSpacing: 0.4,
    color: "#ffffff",
    whiteSpace: "normal",
    overflow: "hidden",
},
  channelSub: {
    fontSize: 11,
    fontWeight: 700,
    color: "#3a5a8a",
    textTransform: "uppercase",
    lineHeight: 1.2,
    letterSpacing: 2.7,
    marginTop: 2,
  },
  programCell: {
    minHeight: 82,
    color: "#c8ddf0",
    border: "none",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    textAlign: "left",
    padding: "16px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  programTitle: {
    fontSize: 22,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: 1.2,
    letterSpacing: 1.2,
    color: "#e6f0ff",
    fontFamily: "'Bebas Neue', 'Arial Narrow', sans-serif",
  },
  programSubtitle: {
    fontSize: 14,
    fontWeight: 400,
    color: "#7a9fff",
    opacity: 0.85,
    marginTop: 2,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  staticBadge: {
    position: "absolute",
    left: 12,
    bottom: 12,
    padding: "5px 9px",
    background: "rgba(0,0,0,0.8)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.75)",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 1.5,
    zIndex: 5,
  },
  nextUpScreen: {
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  background: "#050505",
},

nextUpImage: {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: 0.45,
  filter: "blur(1px)",
},

nextUpOverlay: {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "48px",
  background:
    "linear-gradient(90deg, rgba(0,0,0,0.88), rgba(0,0,0,0.42), rgba(0,0,0,0.1))",
  color: "#fff",
},

nextUpEyebrow: {
  fontSize: "18px",
  letterSpacing: "0.22em",
  fontWeight: 800,
  opacity: 0.82,
  marginBottom: "12px",
},

nextUpShow: {
  fontSize: "56px",
  fontWeight: 900,
  lineHeight: 1,
  marginBottom: "10px",
},

nextUpEpisode: {
  fontSize: "28px",
  fontWeight: 700,
  marginBottom: "10px",
},

nextUpMeta: {
  fontSize: "16px",
  opacity: 0.82,
  marginBottom: "18px",
},

nextUpSummary: {
  maxWidth: "720px",
  fontSize: "18px",
  lineHeight: 1.45,
  opacity: 0.9,
},

nextUpCountdown: {
  position: "absolute",
  left: "48px",
  bottom: "36px",
  fontSize: "24px",
  fontWeight: 800,
},
};
